import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  Badge,
  Button,
  LoadingState,
  ErrorState,
  EmptyState,
} from "@/components/design-system";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import {
  connectStatus,
  connectOnboardingLink,
  myLedger,
  formatCents,
} from "@/lib/payments";

const STATUS_BADGE = {
  accrued: { variant: "neutral", label: "Accrued" },
  paid: { variant: "active", label: "Paid" },
  paid_founder: { variant: "founder", label: "Founder paid" },
};

function Stat({ label, value, tone = "navy", testId }) {
  const tones = {
    navy: "text-[#0B1F3A]",
    blue: "text-[#2D7FF9]",
    green: "text-[#7ED957]",
    cyan: "text-[#20C7E8]",
  };
  return (
    <Card className="!p-5">
      <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">{label}</div>
      <div data-testid={testId} className={`mt-2 font-[Montserrat] font-extrabold text-3xl ${tones[tone]}`}>
        {value}
      </div>
    </Card>
  );
}

export default function Payouts() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ status: "loading", err: "" });
  const [account, setAccount] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState(false);

  const roles = user?.roles || [];
  const canOnboard = roles.includes("founder") || roles.includes("ambassador");

  const load = async () => {
    try {
      const [c, l] = await Promise.all([connectStatus(), myLedger()]);
      setAccount(c.account);
      setLedger(l);
      setState({ status: "ready", err: "" });
    } catch (err) {
      setState({ status: "error", err: formatApiError(err) });
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (searchParams.get("onboarding") === "complete") {
      toast.success("Payout onboarding complete.");
    }
  }, [searchParams]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { url } = await connectOnboardingLink();
      window.location.assign(url);
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  if (!canOnboard) {
    return (
      <div data-testid="payouts-not-eligible">
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Payouts</p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Payouts unlock for Founders & Verified Ambassadors.
        </h1>
        <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
          Add a founder role from Settings or apply to become an ambassador to receive revenue splits.
        </p>
        <div className="mt-6"><Link to="/app/settings"><Button variant="secondary">Go to Settings</Button></Link></div>
      </div>
    );
  }

  if (state.status === "loading") return <LoadingState label="Loading payouts…" />;
  if (state.status === "error") return <ErrorState detail={state.err} />;

  const onboarded = account?.payouts_enabled;

  return (
    <div data-testid="payouts-page" className="space-y-8">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Earnings · Payouts</p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Your payout account
        </h1>
        <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
          Connect a Stripe Express account to receive your 85% founder / 12.5% ambassador share every month.
        </p>
      </div>

      <Card data-testid="payouts-connect-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant={onboarded ? "active" : "neutral"} data-testid="payouts-connect-status">
                {onboarded ? "Payouts enabled" : account ? "Onboarding pending" : "Not connected"}
              </Badge>
              {account?.account_id && (
                <span className="text-[11px] font-mono uppercase tracking-widest text-[#0B1F3A]/50">
                  {account.account_id}
                </span>
              )}
            </div>
            <p className="text-sm text-[#0B1F3A]/75 mt-2 max-w-xl">
              {onboarded
                ? "You're all set. Every monthly payout run will send your accrued balance to this account."
                : "Complete Stripe Express onboarding — takes ~30 seconds in the mock sandbox."}
            </p>
            {account?.requirements_currently_due?.length > 0 && (
              <ul className="mt-2 text-xs text-[#0B1F3A]/60 list-disc list-inside">
                {account.requirements_currently_due.slice(0, 3).map((r) => (<li key={r}>Missing: {r}</li>))}
              </ul>
            )}
          </div>
          {!onboarded && (
            <Button variant="primary" onClick={startOnboarding} disabled={busy} data-testid="payouts-start-onboarding">
              {busy ? "Opening…" : account ? "Continue onboarding" : "Start onboarding"}
            </Button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Accrued" value={formatCents(ledger?.totals?.accrued_cents || 0)} tone="cyan" testId="stat-accrued" />
        <Stat label="Paid to date" value={formatCents(ledger?.totals?.paid_cents || 0)} tone="green" testId="stat-paid" />
        <Stat label="Ledger rows" value={ledger?.total || 0} tone="navy" testId="stat-rows" />
      </div>

      <Card>
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Recent revenue rows</h2>
        <p className="text-sm text-[#0B1F3A]/70 mt-1">
          Every recurring invoice creates one row in your ledger. Founder rows show your 85% share;
          ambassador rows show your 12.5% share on referred subscriptions.
        </p>
        <div className="mt-4">
          {(!ledger?.items || ledger.items.length === 0) ? (
            <EmptyState
              title="Nothing yet"
              description="Once someone subscribes (or is referred by you), rows land here."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payouts-ledger-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 border-b border-[#0B1F3A]/10">
                    <th className="py-2 pr-3">Role</th>
                    <th className="py-2 pr-3">Gross</th>
                    <th className="py-2 pr-3">Your share</th>
                    <th className="py-2 pr-3">Period</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.items.map((r) => {
                    const meta = STATUS_BADGE[r.status] || STATUS_BADGE.accrued;
                    return (
                      <tr key={r.id} className="border-b border-[#0B1F3A]/5">
                        <td className="py-2 pr-3">
                          <Badge variant={r.role === "founder" ? "founder" : "ambassador"}>
                            {r.role === "founder" ? "Founder" : "Ambassador"}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/85">{formatCents(r.gross_cents, r.currency)}</td>
                        <td className="py-2 pr-3 font-bold text-[#0B1F3A]">{formatCents(r.your_cents, r.currency)}</td>
                        <td className="py-2 pr-3 text-[#0B1F3A]/70">
                          {new Date(r.period_start).toLocaleDateString()} → {new Date(r.period_end).toLocaleDateString()}
                        </td>
                        <td className="py-2 pr-3"><Badge variant={meta.variant}>{meta.label}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
